import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { NestedPathHelper } from '../helpers/nested-path.helper';

// Keeps a stack of visited routes so the sidebar Back button can retrace the real
// path the user took (calendar -> card -> course -> back -> card -> back -> calendar)
// instead of only remembering a single previous URL (which ping-pongs). Entries are
// deduped per entity/context, so switching tabs within one entity is not a back step.
@Injectable()
export class NavigationHistoryService {
  private static readonly MAX_ENTRIES = 50;
  private stack: string[] = [];

  constructor(private router: Router) {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.record(event.urlAfterRedirects || event.url);
      }
    });
  }

  private record(url: string): void {
    const key = NestedPathHelper.contextKey(url);
    const topKey = this.stack.length ? NestedPathHelper.contextKey(this.stack[this.stack.length - 1]) : null;
    const prevKey = this.stack.length >= 2 ? NestedPathHelper.contextKey(this.stack[this.stack.length - 2]) : null;

    // Same context as the current top (e.g. a tab switch within one entity): update
    // the stored URL in place, don't grow the stack.
    if (topKey !== null && key === topKey) {
      this.stack[this.stack.length - 1] = url;
      return;
    }

    // Navigated back to the previous context: pop the current top.
    if (prevKey !== null && key === prevKey) {
      this.stack.pop();
      this.stack[this.stack.length - 1] = url;
      return;
    }

    this.stack.push(url);
    if (this.stack.length > NavigationHistoryService.MAX_ENTRIES) {
      this.stack.shift();
    }
  }

  // The route one step below the current top — where Back should go.
  getBackTarget(): string | null {
    return this.stack.length >= 2 ? this.stack[this.stack.length - 2] : null;
  }
}
